import { useState, type FormEvent } from "react";

const FORM_ACTION = "https://readdy.ai/api/form/dahhnfroh653ivfvnvrg";

export default function ContactForm() {
  const [status, setStatus] = useState<"idle" | "loading" | "success" | "error">("idle");
  const [message, setMessage] = useState("");
  const [length, setLength] = useState(0);

  const handleSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const form = event.currentTarget;
    const data = new FormData(form);

    if (String(data.get("contact_alt") || "").trim()) {
      setStatus("success");
      setMessage("消息已提交，我们会在两个工作日内回复。");
      form.reset();
      setLength(0);
      return;
    }
    data.delete("contact_alt");

    const params = new URLSearchParams();
    data.forEach((value, key) => {
      params.append(key, String(value));
    });

    setStatus("loading");
    setMessage("");
    try {
      const response = await fetch(FORM_ACTION, {
        method: "POST",
        headers: { "Content-Type": "application/x-www-form-urlencoded" },
        body: params.toString(),
      });
      const responseText = await response.text();
      let parsed: any = null;
      try {
        parsed = JSON.parse(responseText);
      } catch {
        parsed = null;
      }
      const serverMsg =
        parsed?.meta?.message || parsed?.message || parsed?.meta?.detail || responseText || "";
      const isSpam = typeof serverMsg === "string" && serverMsg.toLowerCase().includes("spam");
      if (!response.ok || parsed?.code !== "OK" || isSpam) {
        setStatus("error");
        setMessage(serverMsg ? `提交失败：${serverMsg}` : "提交失败，请稍后重试。");
        return;
      }
      setStatus("success");
      setMessage("消息已提交，我们会在两个工作日内回复。");
      form.reset();
      setLength(0);
    } catch {
      setStatus("error");
      setMessage("网络异常，请检查连接后重试。");
    }
  };

  return (
    <form
      id="contact-form"
      data-readdy-form
      action={FORM_ACTION}
      method="post"
      onSubmit={handleSubmit}
      className="w-full space-y-4"
    >
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
        <label className="block">
          <span className="mb-1.5 block text-sm font-medium text-foreground-800">称呼</span>
          <input
            type="text"
            name="name"
            required
            placeholder="怎么称呼你"
            className="h-11 w-full rounded-md border border-background-300 bg-background-50 px-3.5 text-sm text-foreground-950 outline-none transition-colors placeholder:text-foreground-400 focus:border-primary-400 focus:ring-2 focus:ring-primary-200"
          />
        </label>
        <label className="block">
          <span className="mb-1.5 block text-sm font-medium text-foreground-800">邮箱</span>
          <input
            type="email"
            name="email"
            required
            autoComplete="email"
            placeholder="用于接收回复的邮箱"
            className="h-11 w-full rounded-md border border-background-300 bg-background-50 px-3.5 text-sm text-foreground-950 outline-none transition-colors placeholder:text-foreground-400 focus:border-primary-400 focus:ring-2 focus:ring-primary-200"
          />
        </label>
      </div>
      <label className="block">
        <span className="mb-1.5 block text-sm font-medium text-foreground-800">沟通类型</span>
        <select
          name="subject"
          defaultValue="correction"
          className="h-11 w-full cursor-pointer rounded-md border border-background-300 bg-background-50 px-3 text-sm text-foreground-800 outline-none transition-colors focus:border-primary-400 focus:ring-2 focus:ring-primary-200"
        >
          <option value="correction">内容更正 / 事实核对</option>
          <option value="tip">线索投稿</option>
          <option value="reprint">转载与授权</option>
          <option value="cooperation">内容合作</option>
          <option value="other">其他问题</option>
        </select>
      </label>
      <label className="block">
        <span className="mb-1.5 block text-sm font-medium text-foreground-800">详细说明</span>
        <textarea
          name="message"
          required
          rows={5}
          maxLength={500}
          onChange={(event) => setLength(event.target.value.length)}
          placeholder="请尽量附上公开链接与时间点，方便我们核对（500 字以内）"
          className="w-full resize-none rounded-md border border-background-300 bg-background-50 px-3.5 py-3 text-sm text-foreground-950 outline-none transition-colors placeholder:text-foreground-400 focus:border-primary-400 focus:ring-2 focus:ring-primary-200"
        />
        <span className="mt-1 block text-right text-xs text-foreground-400">{length} / 500</span>
      </label>
      <input
        type="text"
        name="contact_alt"
        className="note-field-alt"
        tabIndex={-1}
        autoComplete="off"
        aria-hidden="true"
        readOnly
      />
      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <p className="text-xs text-foreground-400">提交即表示同意我们仅将该信息用于沟通回复。</p>
        <button
          type="submit"
          disabled={status === "loading"}
          className="inline-flex h-11 shrink-0 cursor-pointer items-center justify-center gap-2 whitespace-nowrap rounded-md bg-primary-500 px-6 text-sm font-medium text-background-50 transition-colors hover:bg-primary-600 disabled:cursor-not-allowed disabled:opacity-70"
        >
          <i className={status === "loading" ? "ri-loader-4-line animate-spin" : "ri-send-plane-line"} />
          {status === "loading" ? "提交中" : "提交消息"}
        </button>
      </div>
      {status !== "idle" && status !== "loading" && (
        <p
          className={`flex items-center gap-1.5 text-sm ${
            status === "success" ? "text-secondary-700" : "text-primary-700"
          }`}
          role={status === "error" ? "alert" : "status"}
        >
          <i className={status === "success" ? "ri-checkbox-circle-line" : "ri-error-warning-line"} />
          {message}
        </p>
      )}
    </form>
  );
}